/**
 * EventsModal Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive modal component for displaying event information with:
 * - Dynamic event fetching by ID with fallback data sources
 * - Rich media content (images, videos, audio, PDFs)
 * - Event navigation and sharing functionality
 * - Responsive design with fullscreen support
 * - Production-ready error handling and loading states
 * - Embedded repository document links with PDF modal integration
 * - Audio/video media player integration
 * - Member tools (copy URL, email, print)
 * 
 * @param {Object} data - Complete event data object
 * @param {boolean} show - Whether to display the modal
 * @param {function} onClose - Callback when modal is closed
 * @param {boolean} allowFullscreen - Whether to allow fullscreen mode
 * @param {boolean} small - Whether to display in small modal mode
 * @param {function} onNext - Callback for next event navigation
 * @param {function} onPrevious - Callback for previous event navigation
 * @param {boolean} hasNext - Whether there is a next event
 * @param {boolean} hasPrevious - Whether there is a previous event
 * @param {Object} nextEvent - Next event data for navigation preview
 * @param {Object} previousEvent - Previous event data for navigation preview
 * @param {boolean} pageMode - Whether rendering in page mode vs modal mode
 * @returns {JSX.Element|null} Modal component or null if not shown
 */
import React, { useEffect, useState, useMemo, useCallback, useRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import 'tippy.js/dist/tippy.css';
import './EventsModal.css';
import { cleanJson, addTargetBlankToLinks, replaceCss } from '../utils/textUtils';
import PDFModal from './PDFModal';
import Tippy from '@tippyjs/react';
import { useAudio } from '../contexts/AudioContext';
import apiHelper from '../utils/apiHelper';
import { FILE_NAMES } from '../utils/constants';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { AuthContext } from '../components/Auth/AuthContext';

/**
 * EventsModal component for displaying comprehensive event information
 * with media integration and navigation capabilities
 */
function EventsModal({ 
    data, 
    show, 
    onClose, 
    allowFullscreen = false, 
    small = false, 
    onNext, 
    onPrevious, 
    hasNext = false, 
    hasPrevious = false, 
    nextEvent = null, 
    previousEvent = null,
    pageMode = false
}) {
    const navigate = useNavigate();
    const { user: [user] } = useContext(AuthContext);
    
    // Audio context from MediaCentre
    const audioContext = useAudio();
    const { playAudio } = audioContext;

    const [eventData, setEventData] = useState(data);
    const [isLoadingEvent, setIsLoadingEvent] = useState(false);

    useEffect(() => {
        let isMounted = true;
        async function fetchFullEventIfNeeded() {
            // Try multiple ID fields - events.json uses 'ID', but search results might use 'listItemId'
            const searchId = data.ID || data.id || data.listItemId;
            
            // Check if this is an unpublished event (shouldn't be in search results, but handle gracefully)
            if (data.CollateralPublishFlag === "False" || data.CollateralPublishFlag === false) {
                setEventData(data); // Use the passed data as-is
                return;
            }
            
            if (searchId && !data.EventDescription_x002f_Narrative) {
                setIsLoadingEvent(true);
                try {
                    const eventsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.EVENTS);
                    const found = (eventsResult.fileContent || []).find(ev => String(ev.ID) === String(searchId));
                    if (isMounted && found) setEventData(found);
                    else if (isMounted) setEventData(data); // fallback
                } catch (e) {
                    if (isMounted) setEventData(data);
                }
                if (isMounted) setIsLoadingEvent(false);
            } else {
                setEventData(data);
            }
        }
        fetchFullEventIfNeeded();
        return () => { isMounted = false; };
    }, [data]);

    const { 
        title, 
        description, 
        eventDate, 
        startTime, 
        endTime, 
        eventLocation, 
        eventCategory, 
        inPersonVirtual, 
        speakers, 
        agenda, 
        topics,
        eventSponsors,
        fileCollateral,
        videoCollateral,
        audioCollateral,
        registrationLink,
        agendaLink
    } = useMemo(() => ({
        title: eventData.Title,
        description: eventData.EventDescription_x002f_Narrative,
        eventDate: eventData.EventDate,
        startTime: eventData.StartTime,
        endTime: eventData.EndTime,
        eventLocation: eventData.EventLocation,
        eventCategory: eventData.EventCategory,
        inPersonVirtual: eventData.InPerson_x002f_Virtual,
        speakers: eventData.Speakers,
        agenda: eventData.Agenda,
        topics: Array.isArray(eventData.Topics) ? eventData.Topics : 
                (eventData.Topics ? [eventData.Topics] : []),
        eventSponsors: eventData.EventSponsors,
        fileCollateral: eventData.FileCollateral,
        videoCollateral: eventData.VideoCollateral,
        audioCollateral: eventData.AudioCollateral,
        registrationLink: eventData.RegistrationLink,
        agendaLink: eventData.AgendaLink
    }), [eventData]);



    const [eventImage, setEventImage] = useState(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [showRepositoryPdfModal, setShowRepositoryPdfModal] = useState(false);
    const [repositoryPdfData, setRepositoryPdfData] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [copyUrlText, setCopyUrlText] = useState('Copy URL');
    const modalRef = useRef(null);
    
    // Video/Audio modal states (from MediaCentre)
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [videoSource, setVideoSource] = useState(null);
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const [isVideoEmbedded, setIsVideoEmbedded] = useState(false);
    const currentBlobUrl = useRef(null);
    const loadingRef = useRef(false);

    let modalHeight = '600px';
    let modalWidth = '1100px';
    let maxWidth = '90vw';

    if (description && description.length > 4500) {
        modalHeight = '700px';
    }



    const getRandomImage = () => {
        return `/assets/img/news/weekly/${Math.floor(Math.random() * 30) + 1}.jpg`;
    };

    const loadEventImage = async () => {
        if (!eventData.ImageLink) {
            setEventImage("/images/news-reading.jpg");
            return;
        }

        // Check if it's already a blob URL
        if (eventData.ImageLink.startsWith('blob:')) {
            setEventImage(eventData.ImageLink);
            return;
        }

        // Check if it's an HTTPS URL
        if (eventData.ImageLink.includes('https://')) {
            setEventImage(eventData.ImageLink);
            return;
        }

        // Check if it's a local asset path
        if (eventData.ImageLink.includes('assets/img/') || eventData.ImageLink.startsWith('/images/')) {
            setEventImage(eventData.ImageLink);
            return;
        }

        // Otherwise, try to load via API like NewsModal does
        try {
            const authToken = await apiHelper.getAuthToken();
            const imageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${eventData.ImageLink}`;
            
            const response = await fetch(imageUrl, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });

            if (response.ok) {
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                setEventImage(blobUrl);
            } else {
                // Fallback to default image
                setEventImage("/images/news-reading.jpg");
            }
        } catch (error) {
            // Fallback to default image
            setEventImage("/images/news-reading.jpg");
        }
    };

    useEffect(() => {
        loadEventImage();
    }, [eventData.ImageLink, eventData.ID]);

    // Log when modal opens
    useEffect(() => {
        if (show && title && user?.profile?.email) {
            try {
                apiHelper.logModalOpen(user.profile.email, 'EventsModal', title, 'Event');
            } catch (error) {
                // Silent fail - don't disrupt user experience
            }
        }
    }, [show, title, user?.profile?.email]);

    // Cleanup blob URLs when component unmounts or image changes
    useEffect(() => {
        return () => {
            if (eventImage && eventImage.startsWith('blob:')) {
                URL.revokeObjectURL(eventImage);
            }
        };
    }, [eventImage]);

    useEffect(() => {
        if (show && modalRef.current && !isFullScreen) {
            const modalElement = modalRef.current;

            modalElement.style.position = '';
            modalElement.style.top = '';
            modalElement.style.left = '';
            modalElement.style.transform = '';
            modalElement.style.zIndex = '';
        }
    }, [show, isFullScreen]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement
            );
            
            if (isFullScreen && !isCurrentlyFullscreen) {
                setIsFullScreen(false);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, [isFullScreen]);

    const toggleFullScreen = () => {
        if (!isFullScreen) {
            if (modalRef.current) {
                if (modalRef.current.requestFullscreen) {
                    modalRef.current.requestFullscreen();
                } else if (modalRef.current.webkitRequestFullscreen) {
                    modalRef.current.webkitRequestFullscreen();
                } else if (modalRef.current.msRequestFullscreen) {
                    modalRef.current.msRequestFullscreen();
                }
                setIsFullScreen(true);
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
            setIsFullScreen(false);
        }
    };

    const handleFileCollateralClick = async (event, linkElement) => {
        try {
            event.preventDefault();
            event.stopPropagation();

            const fileUrl = linkElement.getAttribute('href') || 
                           linkElement.getAttribute('data-file-url') ||
                           fileCollateral;
            
            // Enhanced filename extraction for non-CDN files
            let fileName = linkElement.getAttribute('data-file-name');
            if (!fileName) {
                // Try to extract from URL
                if (fileUrl) {
                    // Handle URLs with query parameters
                    const urlParts = fileUrl.split('?')[0].split('/');
                    fileName = urlParts[urlParts.length - 1];
                    
                    // Decode URL encoding
                    try {
                        fileName = decodeURIComponent(fileName);
                    } catch (e) {
                        // Keep original filename if decoding fails
                    }
                    
                    // If still no meaningful filename, create a generic one
                    if (!fileName || fileName.length < 3) {
                        fileName = 'Event Document';
                    }
                } else {
                    fileName = 'Event Document';
                }
            }

            // Clean up filename by removing common unwanted characters/patterns
            fileName = fileName.replace(/^direct-/, '').replace(/\.(aspx|ashx).*$/, '');

            // Determine media type and handle accordingly
            if (isVideoFile(fileUrl) || isVideoFile(fileName)) {
                
                // Handle video files
                let videoFilename = fileUrl;
                
                // If it's a local file, extract just the filename
                if (!fileUrl.includes('http://') && !fileUrl.includes('https://')) {
                    if (fileUrl.includes('/')) {
                        videoFilename = fileUrl.split('/').pop();
                    }
                }
                
                // Create video media item
                const videoMediaItem = {
                    video: videoFilename,
                    topic: `${title} - Video Material`,
                    mediaType: 'Video',
                    webURLLink: fileUrl
                };
                
                // Capture mouse position for modal positioning
                let mousePosition = null;
                if (event) {
                    mousePosition = {
                        x: event.clientX,
                        y: event.clientY,
                        scrollY: window.scrollY
                    };
                }
                
                setSelectedVideo(videoMediaItem);
                setShowVideoModal(true);
                if (apiHelper._cachedUserEmail) {
                    apiHelper.logModalOpen(apiHelper._cachedUserEmail, 'VideoModal', title || 'Video', 'Video');
                }

                // Position modal at viewport center
                if (mousePosition) {
                    setTimeout(() => {
                        const videoModal = document.querySelector('.video-modal');
                        const videoOverlay = document.querySelector('.video-modal-overlay');
                        
                        if (videoModal && videoOverlay) {
                            videoOverlay.style.alignItems = 'center';
                            videoOverlay.style.justifyContent = 'center';
                            videoOverlay.style.padding = '0';
                            
                            videoModal.style.position = 'fixed';
                            videoModal.style.top = '50%';
                            videoModal.style.left = '50%';
                            videoModal.style.transform = 'translate(-50%, -50%)';
                        }
                    }, 150);
                }
                
            } else if (isAudioFile(fileUrl) || isAudioFile(fileName)) {
                if (!playAudio) {
                    alert('Audio player is not available. Please refresh the page.');
                    return;
                }
                
                // Capture mouse position for modal positioning
                let mousePosition = null;
                if (event) {
                    mousePosition = {
                        x: event.clientX,
                        y: event.clientY,
                        scrollY: window.scrollY
                    };
                }
                
                // Handle audio files
                if (fileUrl.includes('http://') || fileUrl.includes('https://')) {
                    // External audio URL
                    const audioMediaItem = {
                        topic: `${title} - Audio Material`,
                        shortDescription: 'Event audio material',
                        mediaType: 'Audio',
                        webURLLink: fileUrl,
                        publishDate: new Date(eventData.EventDate).getTime() ? eventData.EventDate : new Date(),
                        entity: 'Irish Funds',
                        duration: null
                    };
                    
                    playAudio(audioMediaItem, mousePosition);
                } else {
                    // Local audio file - need to load it first
                    let audioFilename = fileUrl;
                    if (fileUrl.includes('/')) {
                        audioFilename = fileUrl.split('/').pop();
                    }
                    
                    try {
                        // Try streaming endpoint first
                        const baseUrl = process.env.REACT_APP_API_URL.replace('/api/', '/');
                        const streamingUrl = `${baseUrl}media/stream/${audioFilename}`;
                        
                        const testResponse = await fetch(streamingUrl, {
                            method: 'HEAD'
                        });
                        
                        let audioUrl = null;
                        
                        if (testResponse.ok || testResponse.status === 206) {
                            audioUrl = streamingUrl;
                        } else {
                            // Fallback to blob download
                            const { fileContent } = await apiHelper.fetchAudioByName(audioFilename);
                            audioUrl = URL.createObjectURL(fileContent);
                        }
                        
                        const audioMediaItem = {
                            topic: `${title} - Audio Material`,
                            shortDescription: 'Event audio material',
                            mediaType: 'Audio',
                            webURLLink: audioUrl,
                            originalWebURLLink: fileUrl,
                            publishDate: new Date(eventData.EventDate).getTime() ? eventData.EventDate : new Date(),
                            entity: 'Irish Funds',
                            duration: null
                        };
                        
                        playAudio(audioMediaItem, mousePosition);
                        
                    } catch (error) {
                        alert('Failed to load audio file. Please try again.');
                    }
                }
                
            } else {
                // Handle PDF/document files (existing logic)
                // Ensure we have a file extension for PDFs
                if (!fileName.toLowerCase().includes('.pdf') && !fileName.toLowerCase().includes('.doc')) {
                    fileName += '.pdf'; // Assume PDF if no extension
                }

                const pdfData = {
                    id: eventData.ID || 'unknown',
                    title: `${title} - ${fileName}`,
                    url: fileUrl,
                    filename: fileName
                };

                setRepositoryPdfData(pdfData);
                setShowRepositoryPdfModal(true);
            }
            
        } catch (error) {
            // Silently handle errors in file collateral click
        }
    };

    // Media type detection functions (from MediaCentre)
    const getFileExtension = (filename) => {
        if (!filename) return '';
        const lastDot = filename.lastIndexOf('.');
        return lastDot !== -1 ? filename.substring(lastDot + 1).toLowerCase() : '';
    };

    const isVideoFile = (filename) => {
        if (!filename) return false;
        const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];
        const ext = getFileExtension(filename);
        return videoExtensions.includes(ext) || 
               filename.includes('youtube.com') || 
               filename.includes('youtu.be') || 
               filename.includes('vimeo.com');
    };

    const isAudioFile = (filename) => {
        if (!filename) return false;
        const audioExtensions = ['mp3', 'wav', 'aac', 'ogg', 'flac', 'm4a', 'wma'];
        const ext = getFileExtension(filename);
        return audioExtensions.includes(ext) || 
               filename.includes('spotify.com') || 
               filename.includes('soundcloud.com') || 
               filename.includes('anchor.fm') || 
               filename.includes('pod.link') || 
               filename.includes('goloudplayer.com');
    };

    const isPdfFile = (filename) => {
        if (!filename) return false;
        const pdfExtensions = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'];
        const ext = getFileExtension(filename);
        return pdfExtensions.includes(ext) || 
               filename.includes('.pdf') || 
               filename.includes('.doc') || 
               filename.includes('.aspx') || 
               filename.includes('.ashx');
    };

    // Video loading logic (from MediaCentre)
    useEffect(() => {
        const loadVideo = async () => {
            if (loadingRef.current || !selectedVideo?.video) return;

            loadingRef.current = true;
            setIsVideoLoading(true);
            setVideoSource(null);
            
            const video = selectedVideo.video;
            
            if (video.includes('https://')) {
                // Check if it's a YouTube or Vimeo URL that needs iframe embedding
                const isYouTube = video.includes('youtube.com') || video.includes('youtu.be');
                const isVimeo = video.includes('vimeo.com');

                if (isYouTube || isVimeo) {
                    setVideoSource(getEmbeddableVideoUrl(video));
                    setIsVideoEmbedded(true);
                } else {
                    setVideoSource(video);
                    setIsVideoEmbedded(false);
                }

                setIsVideoLoading(false);
                loadingRef.current = false;
            } else {
                const baseUrl = process.env.REACT_APP_API_URL.replace('/api/', '/');
                const sasEndpoint = `${baseUrl}media/sas/${encodeURIComponent(video)}`;

                try {
                    const resp = await fetch(sasEndpoint);
                    const data = resp.ok ? await resp.json() : null;

                    if (data && data.url) {
                        setVideoSource(data.url);
                        setIsVideoEmbedded(false);
                    } else {
                        const { fileContent } = await apiHelper.fetchVideoByName(video);
                        const newBlobUrl = URL.createObjectURL(fileContent);
                        currentBlobUrl.current = newBlobUrl;
                        setVideoSource(newBlobUrl);
                        setIsVideoEmbedded(false);
                    }
                } catch (error) {
                    try {
                        const { fileContent } = await apiHelper.fetchVideoByName(video);
                        const newBlobUrl = URL.createObjectURL(fileContent);
                        currentBlobUrl.current = newBlobUrl;
                        setVideoSource(newBlobUrl);
                        setIsVideoEmbedded(false);
                    } catch (blobError) {
                        // Both streaming and blob download failed
                    }
                }
                
                setIsVideoLoading(false);
                loadingRef.current = false;
            }
        };

        if (selectedVideo?.video) {
            loadVideo();
        }

        return () => {
            if (currentBlobUrl.current) {
                URL.revokeObjectURL(currentBlobUrl.current);
                currentBlobUrl.current = null;
            }
            loadingRef.current = false;
        };
    }, [selectedVideo?.video]);

    // Helper function to convert video URLs to embeddable format (from MediaCentre)
    const getEmbeddableVideoUrl = (url) => {
        if (url.includes('youtube.com/watch?v=')) {
            const videoId = url.split('v=')[1].split('&')[0];
            return `https://www.youtube.com/embed/${videoId}`;
        } else if (url.includes('youtu.be/')) {
            const videoId = url.split('youtu.be/')[1].split('?')[0];
            return `https://www.youtube.com/embed/${videoId}`;
        } else if (url.includes('vimeo.com/')) {
            const videoId = url.split('vimeo.com/')[1].split('?')[0];
            return `https://player.vimeo.com/video/${videoId}`;
        }
        return url;
    };

    // Get Vimeo embed URL (from MediaCentre)
    const getVimeoEmbedUrl = (vimeoUrl) => {
        const match = vimeoUrl.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
        if (!match) return null;
        
        const videoId = match[1];
        const privacyHash = match[2];
        
        let embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0`;
        
        if (privacyHash) {
            embedUrl += `&h=${privacyHash}`;
        }
        
        return embedUrl;
    };

    // Close video modal
    const closeVideoModal = () => {
        setShowVideoModal(false);
        setSelectedVideo(null);
        
        // Clean up video state
        setVideoSource(null);
        setIsVideoLoading(true);
        setIsVideoEmbedded(false);
        
        // Clean up blob URLs
        if (currentBlobUrl.current) {
            URL.revokeObjectURL(currentBlobUrl.current);
            currentBlobUrl.current = null;
        }
        
        loadingRef.current = false;
    };

    // Toggle fullscreen for video modal
    const toggleVideoFullscreen = () => {
        const videoModal = document.querySelector('.video-modal');
        
        if (!document.fullscreenElement) {
            if (videoModal.requestFullscreen) {
                videoModal.requestFullscreen();
            } else if (videoModal.webkitRequestFullscreen) {
                videoModal.webkitRequestFullscreen();
            } else if (videoModal.msRequestFullscreen) {
                videoModal.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
    };

    const handlePdfModalOpen = () => {
        setShowPdfModal(true);
    };

    const handleOpenEventPage = () => {
        onClose();
        navigate(`/events/${eventData.ID}`, {
            state: {
                currentEvent: eventData
            }
        });
    };

    const handleImageClick = useCallback(() => {
        setShowImageModal(true);
        if (apiHelper._cachedUserEmail) {
            apiHelper.logModalOpen(apiHelper._cachedUserEmail, 'ImageModal', title || 'Event Image', 'Image');
        }
    }, [title]);

    const handlePrintEvent = useCallback(() => {
        const printWindow = window.open('', '_blank', 'width=800,height=600');
        
        const eventContent = document.querySelector('.events-modal-body');
        if (!eventContent) return;
        
        const contentClone = eventContent.cloneNode(true);
        
        const elementsToRemove = contentClone.querySelectorAll(`
            .inline-action-toolbar,
            .member-tools-section,
            .image-overlay,
            .clickable-image .image-container
        `);
        
        elementsToRemove.forEach(el => el.remove());
        
        const printHTML = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Print: ${title}</title>
                <style>
                    body {
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        line-height: 1.6;
                        color: #333;
                        max-width: 800px;
                        margin: 0 auto;
                        padding: 20px;
                    }
                    .events-modal-metadata-section {
                        background: #f8f9fa;
                        border: 1px solid #ddd;
                        padding: 1rem;
                        margin-bottom: 1rem;
                    }
                    .event-type-badge, .category-badge {
                        display: inline-block;
                        padding: 0.25rem 0.5rem;
                        border: 1px solid #333;
                        margin-right: 0.5rem;
                        font-size: 0.8rem;
                        font-weight: bold;
                    }
                    .event-date, .event-time, .event-location {
                        font-size: 0.9rem;
                        color: #666;
                        margin: 0.25rem 0;
                    }
                    .event-description {
                        font-size: 12pt;
                        line-height: 1.5;
                    }
                    @media print {
                        body { margin: 0; padding: 15px; }
                    }
                </style>
            </head>
            <body>
                <h1>${title}</h1>
                ${contentClone.innerHTML}
                <hr style="margin-top: 2rem; border: none; border-top: 1px solid #ddd;">
                <p style="font-size: 0.8rem; color: #666; text-align: center;">
                    Printed from Member Portal - Events
                </p>
            </body>
            </html>
        `;
        
        printWindow.document.write(printHTML);
        printWindow.document.close();
        
        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 250);
        };
    }, [title]);

    const handleCopyUrl = useCallback(async () => {
        try {
            const eventUrl = `${window.location.origin}/events/${eventData.ID}`;
            
            await navigator.clipboard.writeText(eventUrl);
            setCopyUrlText('Copied!');
            setTimeout(() => setCopyUrlText('Copy URL'), 2000);
        } catch (err) {
            const eventUrl = `${window.location.origin}/events/${eventData.ID}`;
            
            const textArea = document.createElement('textarea');
            textArea.value = eventUrl;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopyUrlText('Copied!');
                setTimeout(() => setCopyUrlText('Copy URL'), 2000);
            } catch (fallbackErr) {
                setCopyUrlText('Copy failed');
                setTimeout(() => setCopyUrlText('Copy URL'), 2000);
            }
            document.body.removeChild(textArea);
        }
    }, [eventData.ID]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (showVideoModal) {
                    closeVideoModal();
                } else if (showImageModal) {
                    setShowImageModal(false);
                } else {
                    onClose();
                }
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose, showImageModal, showVideoModal]);

    const formatEventDate = (date) => {
        if (!date) return '';
        try {
            const eventDateObj = new Date(date);
            if (isNaN(eventDateObj.getTime())) {
                return 'Date TBD';
            }
            return eventDateObj.toLocaleDateString('en-GB', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            return 'Date TBD';
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        try {
            const time = new Date(`1970-01-01T${timeString}`);
            return time.toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        } catch {
            return timeString;
        }
    };

    if (!show && !pageMode) return null;
    if (isLoadingEvent) {
        return <LoadingSpinner />;
    }

    const modalContent = (
        <div ref={modalRef} className={`events-modal ${isFullScreen ? 'fullscreen' : ''} ${small ? 'small-modal' : ''} ${pageMode ? 'page-mode' : ''}`}>
            <div className="events-modal-content" style={!isFullScreen ? {
                overflow: 'hidden',
                height: modalHeight,
                width: modalWidth,
                maxWidth: maxWidth
            } : {}}>
                {!pageMode && (
                    <div className="modal-header">
                        <h2 id="modalTitle">{title}</h2>
                        <div>
                            {allowFullscreen && (
                                <span
                                    className={`modalfullscreen fullscreen-btn fas ${isFullScreen ? 'fa-window-restore' : 'fa-expand'}`}
                                    onClick={toggleFullScreen}
                                    title={isFullScreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                                ></span>
                            )}
                            <span
                                className="modalclose pointer fas fa-times"
                                onClick={onClose}
                            ></span>
                        </div>
                    </div>
                )}
                
                <div className="events-modal-body">
                    <div className="events-modal-metadata-section">
                        <div className="metadata-row-primary">
                            <div className="metadata-badges">
                                {inPersonVirtual && (
                                    <span className={`event-type-badge ${inPersonVirtual.toLowerCase().replace(' ', '-')}`}>
                                        {inPersonVirtual}
                                    </span>
                                )}
                                
                                {eventCategory && (
                                    <span className={`category-badge ${replaceCss(eventCategory)}`}>
                                        {eventCategory}
                                    </span>
                                )}
                            </div>
                            
                            {/* Actions */}
                            <div className="metadata-actions-section">
                                <div className="inline-action-toolbar">
                                    <Tippy content={copyUrlText === 'Copy URL' ? 'Copy event URL' : 'URL copied!'} placement="bottom">
                                        <button
                                            className={`inline-action-btn ${copyUrlText !== 'Copy URL' ? 'success' : ''}`}
                                            onClick={handleCopyUrl}
                                        >
                                            <i className={`fas ${copyUrlText !== 'Copy URL' ? 'fa-check' : 'fa-link'}`}></i>
                                        </button>
                                    </Tippy>
                                    
                                    {!pageMode && (
                                        <Tippy content="Open in dedicated page" placement="bottom">
                                            <button
                                                className="inline-action-btn"
                                                onClick={handleOpenEventPage}
                                            >
                                                <i className="fas fa-external-link-alt"></i>
                                            </button>
                                        </Tippy>
                                    )}
                                    
                                    {fileCollateral && (
                                        <Tippy content="Open event documents" placement="bottom">
                                            <button
                                                className="inline-action-btn"
                                                onClick={handlePdfModalOpen}
                                            >
                                                <i className="fas fa-file-pdf"></i>
                                            </button>
                                        </Tippy>
                                    )}
                                    
                                    {registrationLink && (
                                        <Tippy content="Register for event" placement="bottom">
                                            <button
                                                className="inline-action-btn register-action-btn"
                                                onClick={() => window.open(registrationLink, '_blank')}
                                            >
                                                <i className="fas fa-user-plus"></i>
                                            </button>
                                        </Tippy>
                                    )}
                                    
                                    {agendaLink && (
                                        <Tippy content="View agenda" placement="bottom">
                                            <button
                                                className="inline-action-btn agenda-action-btn"
                                                onClick={() => window.open(agendaLink, '_blank')}
                                            >
                                                <i className="fas fa-list-alt"></i>
                                            </button>
                                        </Tippy>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <div className="metadata-row-secondary">
                            {/* Event Date */}
                            <div className="event-date">
                                <i className="fas fa-calendar-alt"></i>
                                {eventDate ? (
                                    (() => {
                                        try {
                                            const eventDateObj = new Date(eventDate);
                                            if (isNaN(eventDateObj.getTime())) {
                                                return 'Date TBD';
                                            }
                                            return eventDateObj.toLocaleDateString('en-GB', {
                                                weekday: 'long',
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            });
                                        } catch (error) {
                                            return 'Date TBD';
                                        }
                                    })()
                                ) : 'Date TBD'}
                            </div>
                            
                            {/* Event Time */}
                            {(startTime || endTime) && (
                                <div className="event-time">
                                    <i className="fas fa-clock"></i>
                                    {startTime && startTime !== 'TBC' ? (
                                        (() => {
                                            try {
                                                const time = new Date(`1970-01-01T${startTime}`);
                                                if (isNaN(time.getTime())) {
                                                    return startTime; // Return original if can't parse
                                                }
                                                return time.toLocaleTimeString('en-GB', {
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: false
                                                });
                                            } catch (error) {
                                                return startTime; // Return original if error
                                            }
                                        })()
                                    ) : startTime === 'TBC' ? 'TBC' : ''}
                                    {startTime && endTime && startTime !== 'TBC' && endTime !== 'TBC' && ' - '}
                                    {endTime && endTime !== 'TBC' ? (
                                        (() => {
                                            try {
                                                const time = new Date(`1970-01-01T${endTime}`);
                                                if (isNaN(time.getTime())) {
                                                    return endTime; // Return original if can't parse
                                                }
                                                return time.toLocaleTimeString('en-GB', {
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                    hour12: false
                                                });
                                            } catch (error) {
                                                return endTime; // Return original if error
                                            }
                                        })()
                                    ) : endTime === 'TBC' ? 'TBC' : ''}
                                </div>
                            )}
                            
                            {/* Event Location */}
                            {eventLocation && (
                                <div className="event-location">
                                    <i className="fas fa-map-marker-alt"></i>
                                    {eventLocation}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Event Action Buttons */}
                    {(registrationLink || agendaLink) && (
                        <div className="event-action-buttons">
                            {registrationLink && (
                                <button 
                                    className="event-register-btn"
                                    onClick={() => window.open(registrationLink, '_blank')}
                                >
                                    <i className="fas fa-user-plus"></i>
                                    Register for Event
                                </button>
                            )}
                            
                            {agendaLink && (
                                <button 
                                    className="event-agenda-btn"
                                    onClick={() => window.open(agendaLink, '_blank')}
                                >
                                    <i className="fas fa-list-alt"></i>
                                    View Agenda
                                </button>
                            )}
                        </div>
                    )}

                    <div className="events-layout">
                        <div className="event-content-left">
                            {/* About This Event - moved to top */}
                            {description && (
                                <div className="event-description">
                                    <h4><i className="fas fa-info-circle"></i> About This Event</h4>
                                    <div dangerouslySetInnerHTML={{ 
                                        __html: addTargetBlankToLinks(cleanJson(description)) 
                                    }} />
                                </div>
                            )}

                            {/* Agenda */}
                            {agenda && (() => {
                                // Check if agenda contains meaningful content
                                const cleanedAgenda = agenda
                                    .replace(/<[^>]*>/g, '') // Remove all HTML tags
                                    .replace(/&nbsp;/g, ' ') // Replace HTML spaces
                                    .trim(); // Remove whitespace
                                
                                return cleanedAgenda.length > 0;
                            })() && (
                                <div className="event-agenda">
                                    <h4><i className="fas fa-list"></i> Agenda</h4>
                                    <div className="summary agenda-summary" dangerouslySetInnerHTML={{ 
                                        __html: addTargetBlankToLinks(cleanJson(agenda)) 
                                    }} />
                                </div>
                            )}

                            {/* Speakers */}
                            {speakers && (
                                <div className="event-speakers">
                                    <h4><i className="fas fa-microphone"></i> Speakers</h4>
                                    <div className="speakers-list">
                                        {speakers.split('\n').filter(speaker => speaker.trim()).map((speaker, index) => (
                                            <div key={index} className="speaker-item">
                                                <i className="fas fa-user-tie"></i>
                                                {speaker.trim()}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Event Sponsors */}
                            {eventSponsors && (() => {
                                // Check if sponsors contains meaningful content
                                const cleanedSponsors = eventSponsors
                                    .replace(/<[^>]*>/g, '') // Remove all HTML tags
                                    .replace(/&nbsp;/g, ' ') // Replace HTML spaces
                                    .trim(); // Remove whitespace
                                
                                return cleanedSponsors.length > 0;
                            })() && (
                                <div className="event-sponsors">
                                    <h4><i className="fas fa-handshake"></i> Event Sponsors</h4>
                                    <div dangerouslySetInnerHTML={{ 
                                        __html: (() => {
                                            // Format sponsors with proper line breaks
                                            let formattedSponsors = cleanJson(eventSponsors);
                                            
                                            // Handle various newline formats
                                            formattedSponsors = formattedSponsors
                                                .replace(/\\n/g, '<br>') // Convert literal \n
                                                .replace(/\n/g, '<br>') // Convert actual newlines
                                                .replace(/\r\n/g, '<br>') // Convert Windows line endings
                                                .replace(/\r/g, '<br>') // Convert Mac line endings
                                                .replace(/(<br>\s*){3,}/g, '<br><br>') // Clean up excessive line breaks
                                                .replace(/<br>\s*$/, ''); // Remove trailing line break
                                            
                                            return addTargetBlankToLinks(formattedSponsors);
                                        })()
                                    }} />
                                </div>
                            )}



                            {/* Topics - moved to bottom */}
                            {topics && topics.length > 0 && (
                                <div className="event-topics-section">
                                    <h4><i className="fas fa-tags"></i> Topics</h4>
                                    <div className="topics-list">
                                        {topics.map((topic, index) => (
                                            <span key={index} className="topic-tag">
                                                {topic}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="event-media-right">
                            {/* Event Image */}
                            <div className="event-image clickable-image">
                                <div className="image-container" onClick={handleImageClick} title="Click to view image in window">
                                    <img src={eventImage} alt={title} />
                                    <div className="image-overlay">
                                        <i className="fas fa-expand"></i>
                                    </div>
                                </div>
                            </div>

                            {/* Event Collateral - Documents, Video, Audio */}
                            {(fileCollateral || videoCollateral || audioCollateral) && (
                                <div className="event-collateral-right">
                                    <h4><i className="fas fa-download"></i> Event Materials</h4>
                                    <div className="collateral-links">
                                        {/* File/Document Collateral */}
                                        {fileCollateral && (
                                            <button 
                                                onClick={(e) => handleFileCollateralClick(e, e.currentTarget)}
                                                className="collateral-link collateral-button pdf-collateral"
                                                data-file-url={fileCollateral}
                                                data-file-name="Event Document"
                                                title="Click to view document"
                                            >
                                                <i className="fas fa-file-pdf"></i>
                                                <span className="collateral-text">
                                                    {(() => {
                                                        // Extract and clean up the filename for display
                                                        let displayName = fileCollateral;
                                                        
                                                        // Get the filename from URL
                                                        if (fileCollateral.includes('/')) {
                                                            const urlParts = fileCollateral.split('?')[0].split('/');
                                                            displayName = urlParts[urlParts.length - 1];
                                                        }
                                                        
                                                        // Decode URL encoding
                                                        try {
                                                            displayName = decodeURIComponent(displayName);
                                                        } catch (e) {
                                                            // Keep original if decoding fails
                                                        }
                                                        
                                                        // Clean up common patterns
                                                        displayName = displayName
                                                            .replace(/^direct-/, '') // Remove "direct-" prefix
                                                            .replace(/\.(aspx|ashx).*$/, '') // Remove .aspx/.ashx extensions
                                                            .replace(/[-_]/g, ' ') // Replace hyphens and underscores with spaces
                                                            .replace(/\.(pdf|doc|docx)$/i, '') // Remove file extensions for cleaner display
                                                            .replace(/\s+/g, ' ') // Normalize whitespace
                                                            .trim();
                                                        
                                                        // Capitalize first letter of each word for better readability
                                                        displayName = displayName.replace(/\b\w/g, l => l.toUpperCase());
                                                        
                                                        // Fallback if we end up with empty or very short string
                                                        if (!displayName || displayName.length < 3) {
                                                            displayName = 'Event Document';
                                                        }
                                                        
                                                        return displayName;
                                                    })()}
                                                </span>
                                                <i className="fas fa-external-link-alt collateral-icon"></i>
                                            </button>
                                        )}

                                        {/* Video Collateral */}
                                        {videoCollateral && (
                                            <button 
                                                onClick={(e) => handleFileCollateralClick(e, e.currentTarget)}
                                                className="collateral-link collateral-button video-collateral"
                                                data-file-url={videoCollateral}
                                                data-file-name="Event Video"
                                                title="Click to play video"
                                            >
                                                <i className="fas fa-video"></i>
                                                <span className="collateral-text">Event Video Recording</span>
                                                <i className="fas fa-play collateral-icon"></i>
                                            </button>
                                        )}

                                        {/* Audio Collateral */}
                                        {audioCollateral && (
                                            <button 
                                                onClick={(e) => handleFileCollateralClick(e, e.currentTarget)}
                                                className="collateral-link collateral-button audio-collateral"
                                                data-file-url={audioCollateral}
                                                data-file-name="Event Audio"
                                                title="Click to play audio"
                                            >
                                                <i className="fas fa-microphone"></i>
                                                <span className="collateral-text">Event Audio Recording</span>
                                                <i className="fas fa-headphones collateral-icon"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer with Member Tools and Navigation */}
                <div className="events-modal-footer-nav">
                    <div className="nav-footer-controls">
                        {/* Previous Navigation */}
                        {hasPrevious && (
                            <Tippy
                                content={
                                    previousEvent ? (
                                        <div className="nav-tooltip-content">
                                            <div className="nav-tooltip-title">{previousEvent.Title}</div>
                                            {previousEvent.EventDescription_x002f_Narrative && (
                                                <div className="nav-tooltip-summary">
                                                    {previousEvent.EventDescription_x002f_Narrative.substring(0, 200)}
                                                    {previousEvent.EventDescription_x002f_Narrative.length > 200 ? '...' : ''}
                                                </div>
                                            )}
                                            <div className="nav-tooltip-meta">
                                                {previousEvent.EventDate && (
                                                    <span><i className="fas fa-calendar"></i> {
                                                        (() => {
                                                            try {
                                                                const eventDateObj = new Date(previousEvent.EventDate);
                                                                if (isNaN(eventDateObj.getTime())) {
                                                                    return 'Date TBD';
                                                                }
                                                                return eventDateObj.toLocaleDateString('en-GB', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric'
                                                                });
                                                            } catch (error) {
                                                                return 'Date TBD';
                                                            }
                                                        })()
                                                    }</span>
                                                )}
                                                {previousEvent.EventLocation && (
                                                    <span><i className="fas fa-map-marker-alt"></i> {previousEvent.EventLocation}</span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        'Previous event'
                                    )
                                }
                                placement="top-start"
                                interactive={true}
                                delay={[500, 200]}
                                maxWidth={300}
                                theme="light-border"
                                offset={[20, 10]}
                                boundary="viewport"
                            >
                                <button 
                                    className="nav-footer-btn nav-prev"
                                    onClick={onPrevious}
                                >
                                    <i className="fas fa-chevron-left"></i>
                                    <span className="nav-btn-text">Prev</span>
                                    {previousEvent && (
                                        <span className="nav-btn-title">
                                            {previousEvent.Title?.length > 25 
                                                ? previousEvent.Title.substring(0, 25) + '...' 
                                                : previousEvent.Title}
                                        </span>
                                    )}
                                </button>
                            </Tippy>
                        )}

                        <div className="footer-member-tools">
                            <button 
                                className="footer-tool-btn"
                                onClick={handleCopyUrl}
                                title="Copy URL to share with other members"
                            >
                                <i className="fas fa-link"></i>
                                <span className="tool-text">{copyUrlText}</span>
                            </button>
                            <button 
                                className="footer-tool-btn"
                                onClick={() => {
                                    const subject = encodeURIComponent(`Member Portal Event: ${title}`);
                                    let formattedDate = 'Date TBD';
                                    try {
                                        if (eventDate) {
                                            const eventDateObj = new Date(eventDate);
                                            if (!isNaN(eventDateObj.getTime())) {
                                                formattedDate = eventDateObj.toLocaleDateString('en-GB', {
                                                    weekday: 'long',
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                });
                                            }
                                        }
                                    } catch (error) {
                                        // Use default if error formatting date
                                    }
                                    const body = encodeURIComponent(`Hi,\n\nI thought you might be interested in this event from our member portal:\n\n${title}\n\nDate: ${formattedDate}\nLocation: ${eventLocation || 'TBD'}\n\n${window.location.origin}/events/${eventData.ID}\n\nBest regards`);
                                    window.location.href = `mailto:?subject=${subject}&body=${body}`;
                                }}
                                title="Email to fellow members"
                            >
                                <i className="fas fa-envelope"></i>
                                <span className="tool-text">Email</span>
                            </button>
                            <button 
                                className="footer-tool-btn"
                                onClick={handlePrintEvent}
                                title="Print event details"
                            >
                                <i className="fas fa-print"></i>
                                <span className="tool-text">Print</span>
                            </button>
                        </div>
                        
                        {hasNext && (
                            <Tippy
                                content={
                                    nextEvent ? (
                                        <div className="nav-tooltip-content">
                                            <div className="nav-tooltip-title">{nextEvent.Title}</div>
                                            {nextEvent.EventDescription_x002f_Narrative && (
                                                <div className="nav-tooltip-summary">
                                                    {nextEvent.EventDescription_x002f_Narrative.substring(0, 200)}
                                                    {nextEvent.EventDescription_x002f_Narrative.length > 200 ? '...' : ''}
                                                </div>
                                            )}
                                            <div className="nav-tooltip-meta">
                                                {nextEvent.EventDate && (
                                                    <span><i className="fas fa-calendar"></i> {
                                                        (() => {
                                                            try {
                                                                const eventDateObj = new Date(nextEvent.EventDate);
                                                                if (isNaN(eventDateObj.getTime())) {
                                                                    return 'Date TBD';
                                                                }
                                                                return eventDateObj.toLocaleDateString('en-GB', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric'
                                                                });
                                                            } catch (error) {
                                                                return 'Date TBD';
                                                            }
                                                        })()
                                                    }</span>
                                                )}
                                                {nextEvent.EventLocation && (
                                                    <span><i className="fas fa-map-marker-alt"></i> {nextEvent.EventLocation}</span>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        'Next event'
                                    )
                                }
                                placement="top-end"
                                interactive={true}
                                delay={[500, 200]}
                                maxWidth={300}
                                theme="light-border"
                                offset={[-20, 10]}
                                boundary="viewport"
                            >
                                <button 
                                    className="nav-footer-btn nav-next"
                                    onClick={onNext}
                                >
                                    <span className="nav-btn-text">Next</span>
                                    {nextEvent && (
                                        <span className="nav-btn-title">
                                            {nextEvent.Title?.length > 25 
                                                ? nextEvent.Title.substring(0, 25) + '...' 
                                                : nextEvent.Title}
                                        </span>
                                    )}
                                    <i className="fas fa-chevron-right"></i>
                                </button>
                            </Tippy>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {pageMode ? (
                modalContent
            ) : (
                ReactDOM.createPortal(
                    modalContent,
                    document.body
                )
            )}

            {ReactDOM.createPortal(
                showPdfModal && fileCollateral && (
                    <PDFModal
                        key={eventData.ID}
                        title={title}
                        url={fileCollateral}
                        show={showPdfModal}
                        onClose={() => setShowPdfModal(false)}
                        allowFullscreen={true}
                        allowMove={true}
                        showLinkinToolBar={true}
                    />
                ),
                document.body
            )}

            {ReactDOM.createPortal(
                showImageModal && eventImage && (
                    <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
                        <div className="image-modal-container" onClick={(e) => e.stopPropagation()}>
                            <div className="image-modal-header">
                                <h3>
                                  <Tippy content={title}>
                                    <span>{title.length > 50 ? `${title.substring(0, 50)}...` : title}</span>
                                  </Tippy>
                                </h3>
                                <button 
                                    className="image-modal-close"
                                    onClick={() => setShowImageModal(false)}
                                    title="Close image"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div className="image-modal-content">
                                <img src={eventImage} alt={title} />
                            </div>
                        </div>
                    </div>
                ),
                document.body
            )}

            {ReactDOM.createPortal(
                showRepositoryPdfModal && repositoryPdfData && (
                    <PDFModal
                        key={`event-${repositoryPdfData.id}`}
                        title={repositoryPdfData.title}
                        url={repositoryPdfData.url}
                        show={showRepositoryPdfModal}
                        onClose={() => {
                            setShowRepositoryPdfModal(false);
                            setRepositoryPdfData(null);
                        }}
                        allowFullscreen={true}
                        allowMove={true}
                        showLinkinToolBar={true}
                        listItemId={repositoryPdfData.id}
                        externalUrls={false}
                    />
                ),
                document.body
            )}

            {/* Video Modal (from MediaCentre) */}
            {ReactDOM.createPortal(
                showVideoModal && selectedVideo && (
                    <div className="video-modal-overlay" onClick={closeVideoModal}>
                        <div className="video-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="video-modal-header">
                                <h3>{selectedVideo.topic || selectedVideo.title}</h3>
                                <div className="video-modal-controls">
                                    <button className="fullscreen-btn" onClick={toggleVideoFullscreen} title="Toggle Fullscreen">
                                        <i className="fas fa-expand"></i>
                                        <i className="fas fa-compress"></i>
                                    </button>
                                    <button className="close-btn" onClick={closeVideoModal}>
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            </div>
                            <div className="video-modal-content">
                                {/* Handle Vimeo/YouTube videos for external URLs */}
                                {selectedVideo.webURLLink && (
                                    selectedVideo.webURLLink.includes('vimeo.com') || 
                                    selectedVideo.webURLLink.includes('youtube.com') || 
                                    selectedVideo.webURLLink.includes('youtu.be')
                                ) ? (
                                    <iframe
                                        src={selectedVideo.webURLLink.includes('vimeo.com') 
                                            ? getVimeoEmbedUrl(selectedVideo.webURLLink)
                                            : getEmbeddableVideoUrl(selectedVideo.webURLLink)
                                        }
                                        width="100%"
                                        height="100%"
                                        frameBorder="0"
                                        allow="autoplay; fullscreen; picture-in-picture"
                                        allowFullScreen
                                        title={selectedVideo.topic || selectedVideo.title}
                                    ></iframe>
                                ) : (
                                    /* Handle local blob videos with proper loading */
                                    <>
                                        {isVideoLoading ? (
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                                <LoadingSpinner />
                                            </div>
                                        ) : videoSource ? (
                                            isVideoEmbedded ? (
                                                <iframe
                                                    src={videoSource}
                                                    width="100%"
                                                    height="100%"
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                    title={selectedVideo.topic || selectedVideo.title}
                                                />
                                            ) : (
                                                <video
                                                    width="100%"
                                                    height="100%"
                                                    controls
                                                    autoPlay
                                                    src={videoSource}
                                                    onError={(e) => {
                    // Handle video streaming errors silently
                }}
                                                >
                                                    Your browser does not support the video tag.
                                                </video>
                                            )
                                        ) : (
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#666' }}>
                                                <p>Failed to load video</p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            {selectedVideo.duration && (
                                <div className="video-modal-info">
                                    <div className="video-modal-meta media-meta">
                                        <span><i className="fas fa-clock"></i> {selectedVideo.duration}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ),
                document.body
            )}
        </>
    );
}

export default React.memo(EventsModal, (prevProps, nextProps) => {
    return (
        prevProps.show === nextProps.show &&
        prevProps.data.ID === nextProps.data.ID &&
        prevProps.hasNext === nextProps.hasNext &&
        prevProps.hasPrevious === nextProps.hasPrevious
    );
}); 